import { useCallback, useEffect, useState } from 'react'

import { Box, Button, Section, Spinner, Tag, Text } from '@factorialco/deprecated-design-system'

import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import {
  FactorialIDApiError,
  TotpShowData,
  getTotpStatus,
} from 'modules/factorial_id/lib/api_client'

import { DeprovisionMFAModal } from './deprovisioning_modal'
import { ProvisionMFAModal } from './provisioning_modal'

interface UseTotpDataResult {
  data: TotpShowData | null
  loading: boolean
  error: string | null
  refetch: () => void
}

function useTotpData(): UseTotpDataResult {
  const [data, setData] = useState<TotpShowData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTotpData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const totpData = await getTotpStatus()
      setData(totpData)
    } catch (err) {
      setError(err instanceof FactorialIDApiError ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTotpData()
  }, [fetchTotpData])

  return { data, loading, error, refetch: fetchTotpData }
}

export function MFASettings() {
  const { data, loading, error, refetch } = useTotpData()
  const [showProvisionModal, setShowProvisionModal] = useState(false)
  const [showDeprovisionModal, setShowDeprovisionModal] = useState(false)

  const canSetupMFA = data?.status === 'available' || data?.status === 'provisioning'
  const canDisableMFA = data?.can_deprovision ?? false
  const mfaUnavailable = data?.status === 'unavailable'

  const statusTag = () => {
    if (data?.status === 'provisioned') {
      return <Tag type="viridian">{i18n.t('personal_settings.mfa.active')}</Tag>
    }

    return <Tag type="grey">{i18n.t('personal_settings.mfa.not_configured')}</Tag>
  }

  return (
    <Section
      title={i18n.t('personal_settings.mfa.title')}
      description={i18n.t('personal_settings.mfa.description')}
      illustration="lock-user"
      link={{
        href: helpCenterUrl('mfa'),
        label: i18n.t('personal_settings.mfa.link'),
      }}
    >
      <Box
        alignItems="flexStart"
        borderRadius={{
          all: 'abs008',
        }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        gap="s12"
        padding="s24"
      >
        <Text.Normal size="300" weight="semibold">
          {i18n.t('personal_settings.mfa.setup_title')}
        </Text.Normal>

        {loading && <Spinner />}

        {error && <Text.Normal size="200">Error: {error}</Text.Normal>}

        {data && (
          <>
            {mfaUnavailable ? (
              <Text.Normal>{i18n.t('personal_settings.mfa.not_available')}</Text.Normal>
            ) : (
              <>
                {statusTag()}
                {canSetupMFA && (
                  <Box paddingTop="s12">
                    <Button type="primary" onClick={() => setShowProvisionModal(true)}>
                      {i18n.t('personal_settings.mfa.activate')}
                    </Button>
                  </Box>
                )}
              </>
            )}
            {canDisableMFA && (
              <Box paddingTop="s12">
                <Button type="negative" onClick={() => setShowDeprovisionModal(true)}>
                  {i18n.t('personal_settings.mfa.deactivate')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {showProvisionModal && (
        <ProvisionMFAModal
          onClose={() => setShowProvisionModal(false)}
          onSuccess={() => {
            setShowProvisionModal(false)
            refetch()
          }}
        />
      )}

      {showDeprovisionModal && data && (
        <DeprovisionMFAModal
          passwordRequired={data.deprovision_requires_password}
          onClose={() => setShowDeprovisionModal(false)}
          onSuccess={() => {
            setShowDeprovisionModal(false)
            refetch()
          }}
        />
      )}
    </Section>
  )
}
